/**
 * The sub-category a disputed SKU belongs to.
 *
 * MIRRORS the `sub` field of `freeze-v2/data/noberoFreeze.js`'s `SKUS`, copied
 * rather than imported because the two modules are deliberately independent —
 * the same rule `data/packs.js` and everything under `disputes-v2/freeze/`
 * follows.
 *
 * WHY IT IS HERE AT ALL: a freeze can be agreed for a group of similar products
 * rather than one code at a time, so the drawer has to name which group. Nothing
 * in the dispute dataset records one — a dispute row carries a SKU code, a
 * product name, and no third identifier. The catalogue that does record one is
 * the freeze module's, and it catalogues the same merchant's SKUs, so the
 * sub-category is LOOKED UP BY SKU CODE.
 *
 * THE JOIN IS THE CODE AND NOTHING ELSE. Product names are never matched,
 * loosely or exactly: `1M-BWJG-R0137-BL4` is 'Wanderer - Travel Pants Black / L'
 * and the catalogue files it under Travel Wear, not the Joggers that both its
 * name and its BWJG code suggest. A name-derived sub-category would be a guess
 * wearing the clothes of data.
 *
 * ONLY THE CODES THIS MODULE'S OWN DATASET USES ARE MIRRORED — 24 of the
 * catalogue's 111. The other 87 describe SKUs no dispute row mentions, and a
 * second copy of them would be 87 more rows with nothing reading them and
 * everything to drift.
 *
 * WHAT IS MISSING IS A REAL STATE, NOT A HOLE TO FILL. 24 of the dataset's 25
 * SKU groups (116 of its 120 rows) carry a code the catalogue holds. The 25th —
 * 4 rows of '2-Pack Classic Straight Fit Joggers Navy Blue/Eiffel Tower / L' —
 * carries an EMPTY `sku`, so there is no code to look up and `subcatOf` answers
 * UNCATEGORISED. The drawer says so plainly rather than picking a plausible
 * group for them.
 *
 * DRIFT: this is a second copy. `subcats.test.js` reads the freeze catalogue as
 * text and pins every pair below both ways, so a re-catalogued SKU cannot
 * silently leave this module showing a merchant the wrong group — or quietly
 * leave a newly catalogued one uncategorised.
 */

/** The catalogue's own placeholder, the same word the freeze drawer shows. */
export const UNCATEGORISED = 'Uncategorised'

export const SKU_SUBCAT = {
  '1M-B': 'Joggers',
  '1M-BWCP-R0002-BL3': 'Cargo Pants',
  '1M-BWCP-R0002-BL5': 'Cargo Pants',
  '1M-BWJG-R0084-BL3': 'Joggers',
  '1M-BWJG-R0137-BL4': 'Travel Wear',
  '1M-BWJG-R0149-BL4': 'Joggers',
  '1M-BWJG-R0149-BL6': 'Joggers',
  '1M-BWJG-R0149-ET4': 'Joggers',
  '1M-BWJG-R0149-NB4': 'Joggers',
  '1M-BWJG-R0149-NB5': 'Joggers',
  '1M-BWJG-R0149-PB4': 'Joggers',
  '1M-BWJG-R0149-PB6': 'Joggers',
  '1M-TWPO-R0002-AQB5': 'Polo T-Shirts',
  '1M-TWPO-R0002-DR5': 'Polo T-Shirts',
  '1M-TWPO-R0002-HG4': 'Polo T-Shirts',
  '1M-TWPO-R0002-PN5': 'Polo T-Shirts',
  '1M-TWPO-R0002-WT4': 'Polo T-Shirts',
  '1M-TWTS-C09': 'T-Shirts',
  '1M-TWTS-C0969-OG4': 'T-Shirts',
  '1M-TWTS-C1059-BL4': 'T-Shirts',
  '1M-TWZP-R0002-BL4': 'Hoodies',
  'NBJUN24MJ05 Joggers (Pack of 2)-Men-Joggers-BL/ET-L': 'Joggers',
  'NBJUN24MJ05-Men-Joggers-Black-M': 'Joggers',
  'NBSS24MC10-Men-Coord Set-Navy Blue-L': 'Co-ord Sets',
}

/**
 * Takes a dispute row or a SKU roll-up — both carry `sku` — and reads that field
 * alone. Never `skuKey`, which falls back to the product name for a codeless
 * row: a product name that happened to equal some other SKU's code would then
 * borrow that SKU's sub-category.
 *
 * `hasOwnProperty` rather than a bare lookup because an object literal answers
 * for 'constructor' and 'toString', and a function is not a sub-category.
 */
export function subcatOf(x) {
  const code = (x && x.sku) || ''
  if (code && Object.prototype.hasOwnProperty.call(SKU_SUBCAT, code)) return SKU_SUBCAT[code]
  return UNCATEGORISED
}
